"use client";

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { useGetCoursesQuery } from "@/features/api/courseApi";
import { useGetOrdersQuery } from "@/features/api/paymentApi";

import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";

import { BookOpen, Video, Search } from "lucide-react";
import { motion } from "framer-motion";

/* -------------------- SKELETON -------------------- */
const CourseSkeleton = () => (
  <div className="w-full animate-pulse space-y-4 p-4">
    <Skeleton className="h-48 w-full rounded-xl" />
    <Skeleton className="h-6 w-3/4 rounded" />
    <Skeleton className="h-4 w-1/2 rounded" />
    <Skeleton className="h-12 w-full rounded-xl" />
  </div>
);

/* -------------------- COURSE CARD -------------------- */
const CourseCardWithProgress = ({
  course,
  isEnrolled,
  progress,
  handleOpen,
}) => {
  const percent = Number(progress?.percentage || 0);
  const completed = progress?.completedLessons?.length || 0;
  const total = progress?.totalLessons || 0;

  const buttonText = isEnrolled
    ? percent >= 100
      ? "Review Course"
      : percent > 0
      ? "Continue Learning"
      : "Start Learning"
    : course.price > 0
    ? `View Course – ₹${course.price}`
    : "Explore Free Course";

  const featuredImg =
    typeof course.featuredImage === "string" &&
    course.featuredImage.startsWith("/uploads")
      ? `https://fetc.in${course.featuredImage}`
      : course.featuredImage ||
        "https://dummyimage.com/600x400/e5e7eb/000&text=No+Image";

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white rounded-2xl shadow-lg flex flex-col overflow-hidden"
    >
      <div className="relative">
        <img
          src={featuredImg}
          alt={course.title}
          className="w-full h-48 object-cover"
          loading="lazy"
        />

        {isEnrolled && (
          <span className="absolute top-3 right-3 bg-green-600 text-white text-xs px-3 py-1 rounded-full">
            Enrolled
          </span>
        )}
      </div>

      <div className="p-6 flex flex-col flex-grow">
        <h3 className="font-semibold text-xl line-clamp-2 mb-2">
          {course.title}
        </h3>

        <p className="text-sm text-gray-600 mb-3">
          {course.instructorName || "Expert Instructor"}
        </p>

        <p className="text-2xl font-bold mb-4">
          {course.price > 0 ? `₹${course.price}` : "Free"}
        </p>

        {isEnrolled && (
          <div className="mb-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress</span>
              <span>
                {percent}% {total > 0 && `(${completed}/${total})`}
              </span>
            </div>
            <Progress value={percent} className="h-2" />
          </div>
        )}

        <Button
          size="lg"
          className="w-full mt-auto"
          onClick={handleOpen}
        >
          <BookOpen className="mr-2 h-5 w-5" />
          {buttonText}
        </Button>
      </div>
    </motion.div>
  );
};

/* -------------------- MAIN PAGE -------------------- */
const AllCourses = () => {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  /* FETCH COURSES */
  const { data, isLoading, error } = useGetCoursesQuery();
  const courses = data?.courses || [];

  /* PUBLISHED ONLY */
  const publishedCourses = useMemo(
    () => courses.filter((c) => c.status === "published"),
    [courses]
  );

  /* PAID ORDERS */
  const { data: ordersData } = useGetOrdersQuery(
    { status: "SUCCESS" },
    { skip: !user }
  );

  const enrolledCourseIds = useMemo(() => {
    const set = new Set(user?.enrolledCourses || []);
    ordersData?.data?.forEach((o) => {
      if (o.status === "SUCCESS") {
        set.add(o.courseId || o.productId);
      }
    });
    return set;
  }, [user, ordersData]);

  /* SEARCH */
  const filteredCourses = useMemo(() => {
    if (!searchTerm) return publishedCourses;
    return publishedCourses.filter((c) =>
      [c.title, c.instructorName]
        .filter(Boolean)
        .some((v) =>
          v.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );
  }, [searchTerm, publishedCourses]);

  /* SORT */
  const sortedCourses = useMemo(() => {
    const arr = [...filteredCourses];
    if (sortBy === "newest")
      arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (sortBy === "oldest")
      arr.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    if (sortBy === "priceLow")
      arr.sort((a, b) => (a.price || 0) - (b.price || 0));
    if (sortBy === "priceHigh")
      arr.sort((a, b) => (b.price || 0) - (a.price || 0));
    return arr;
  }, [filteredCourses, sortBy]);

  /* PAGINATION */
  const totalPages = Math.max(
    1,
    Math.ceil(sortedCourses.length / pageSize)
  );

  const paginated = sortedCourses.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  /* OPEN COURSE */
  const openCourse = (course) =>
    navigate(`/courses/${course.slug}`);

  /* -------------------- UI -------------------- */
  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* HEADER */}
        <div className="flex flex-col sm:flex-row justify-between gap-6 mb-10">
          <h2 className="text-4xl font-extrabold">
            📚 Explore Courses
          </h2>

          <div className="relative w-full sm:w-80">
            <Input
              placeholder="Search courses or instructors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12"
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
              <SelectItem value="priceLow">Price: Low to High</SelectItem>
              <SelectItem value="priceHigh">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* GRID */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <CourseSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <p className="text-center text-red-500 text-xl">
            Failed to load courses.
          </p>
        ) : paginated.length === 0 ? (
          <p className="text-center text-gray-600">
            No courses found.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {paginated.map((course) => (
              <CourseCardWithProgress
                key={course._id}
                course={course}
                isEnrolled={enrolledCourseIds.has(course._id)}
                progress={{}} // safe placeholder
                handleOpen={() => openCourse(course)}
              />
            ))}
          </div>
        )}

        {/* PAGINATION */}
        <div className="flex justify-between items-center mt-10">
          <Select
            value={String(pageSize)}
            onValueChange={(v) => {
              setPageSize(Number(v));
              setPage(1);
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="8">8 / page</SelectItem>
              <SelectItem value="12">12 / page</SelectItem>
              <SelectItem value="16">16 / page</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </Button>

            <span className="font-semibold">
              {page} / {totalPages}
            </span>

            <Button
              variant="outline"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllCourses;
